import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { supabase } from "@/lib/supabase";
import {
	JobItemEntityType,
	PatientEntityType,
	SupabaseTableEnum,
} from "@/lib/supabase/supabaseTypes";
import { JobDocumentWithFiles } from "../store/Jobs/job-documents.store";
import { useCentralStore } from "../store/Central";
import { useJobStore } from "../store/Jobs";
import { usePatientStore } from "../store/Patient";
import { documentPropsFor, useExportActions } from "./actions/useExportActions";
import { showNotification } from "../store/Central/selectors";
import { Logger } from "@/lib/logger/Logger";
import { StorageBucketsEnum } from "../types/enums";
import { PdfTemplateType } from "../pdf-templates/document-template.types";
import { getFileNameForDocument } from "./useHandleExportDocument/export-document-utils";
import { LazyPatient } from "../store/Patient/patients.store";
import { createFileMetadata } from "@/lib/utils/file-utils";
import { JobDocumentTypeEnum } from "../pages/job-page/job-document/job-document.types";
import { resolveRegionKind } from "../lib/utils/region-utils";

/**
 * Re-export functionality for already completed/booked/paid documents.
 *
 * Business Context:
 * This is specifically for documents that are already completed, booked, or paid.
 * Re-export allows users to correct mistakes (typos, wrong patient, etc.) in already
 * completed/booked documents without having to create an entirely new job and document (that
 * potentially needs to either be then invalidated again or needs to be corrected via a credit note)
 * Additionally, this allows to send the corrected document in the same job channel as the original document.
 *
 * The re-export ONLY generates a new PDF file - it does NOT change any accounting data, booking status, or amounts.
 * This keeps the accounting intact while allowing document corrections.
 *
 * Implementation:
 * This is a copy of useHandleExportDocument -> handleExportDocument -> for documents
 * but only the part that creates the PDF and saves it to the files table.
 * No document status changes or accounting operations occur.
 */
export const useHandleReExportDocument = () => {
	const [reExportLoading, setReExportLoading] = useState<number | null>(null);

	const { exportLieferschein, exportKostenvoranschlag } = useExportActions();
	const { patientsLookup } = usePatientStore((state) => ({
		patientsLookup: state.patientsLookup,
	}));
	const { job, fetchJobDocument } = useJobStore((state) => ({
		job: state.job,
		fetchJobDocument: state.fetchJobDocument,
	}));
	const { organization, client, activatedRegions } = useCentralStore((state) => ({
		organization: state.organization,
		client: state.client,
		activatedRegions: state.activatedRegions,
	}));

	const handleReExportDocument = async (
		jobItems: JobItemEntityType[],
		jobDocument: JobDocumentWithFiles
	) => {
		// Validation
		if (!job) {
			showNotification({
				message: "Auftrag nicht gefunden. Bitte versuchen Sie es erneut.",
				type: "error",
			});
			return { success: false };
		}
		if (!client) {
			showNotification({
				message: "Kunde nicht gefunden. Bitte versuchen Sie es erneut.",
				type: "error",
			});
			return { success: false };
		}
		if (!organization) {
			showNotification({
				message: "Organisation nicht gefunden. Bitte versuchen Sie es erneut.",
				type: "error",
			});
			return { success: false };
		}

		setReExportLoading(jobDocument.id);

		try {
			// Prepare document props
			const completeJobInformation = {
				client,
				job,
				jobItems,
				jobDocument,
			};

			const documentProps = await documentPropsFor(
				organization,
				completeJobInformation
			);

			if (!documentProps) {
				showNotification({
					message: "Dokumentinformationen konnten nicht erstellt werden.",
					type: "error",
				});
				setReExportLoading(null);
				return { success: false };
			}

			// Determine template type based on document type
			let templateType: PdfTemplateType;
			let documentTypeName: string;

			if (jobDocument.type === JobDocumentTypeEnum.DeliveryNote) {
				templateType = PdfTemplateType.LIEFERSCHEIN;
				documentTypeName = "Lieferschein";
			} else if (jobDocument.type === JobDocumentTypeEnum.Quotation) {
				templateType = PdfTemplateType.KOSTENVORANSCHLAG;
				documentTypeName = "Kostenvoranschlag";
			} else {
				showNotification({
					message: "Dieser Dokumenttyp kann nicht erneut exportiert werden.",
					type: "error",
				});
				setReExportLoading(null);
				return { success: false };
			}

			// Generate file name and path
			const fileName = getFileNameForDocument({
				templateType,
				jobDocument,
				job,
				client,
				patientsLookup: patientsLookup as Record<string, LazyPatient>,
				region: resolveRegionKind(client, organization, activatedRegions),
			});
			const pathName = uuidv4() + "/" + fileName;

			// Get patient if exists
			let patient: PatientEntityType | undefined;
			if (job.patient_id) {
				patient = documentProps.patient;
			}

			// Export the PDF using the appropriate export function
			let exportError: string | null = null;
			if (templateType === PdfTemplateType.LIEFERSCHEIN) {
				const { error } = await exportLieferschein(
					documentProps,
					pathName,
					patient
				);
				exportError = error;
			} else if (templateType === PdfTemplateType.KOSTENVORANSCHLAG) {
				const { error } = await exportKostenvoranschlag(
					documentProps,
					pathName,
					patient
				);
				exportError = error;
			}

			if (exportError) {
				showNotification({
					message: "Fehler beim Erstellen des PDF-Dokuments.",
					type: "error",
					description: exportError,
				});
				Logger.error(`Error re-exporting ${documentTypeName}`, {}, { exportError });
				setReExportLoading(null);
				return { success: false };
			}

			// Save file record to database
			const organizationId = organization.id;
			if (!organizationId) {
				showNotification({
					message: "Organisation ID nicht gefunden. Bitte versuchen Sie es erneut.",
					type: "error",
				});
				Logger.error("Organization ID not found", {}, organization);
				return { success: false };
			}

			const { data: fileData, error: dbError } = await supabase
				.from(SupabaseTableEnum.FILES)
				.insert([
					{
						file_name: fileName,
						bucket_name: StorageBucketsEnum.V1,
						path_name: pathName,
						job_id: job.id,
						meta_data: createFileMetadata(
							{
								name: fileName,
								type: "pdf",
							},
							templateType
						),
						organization_id: organizationId,
						job_document_id: jobDocument.id,
					},
				])
				.select();

			if (dbError) {
				showNotification({
					message: "Fehler beim Speichern der Datei-Information.",
					type: "error",
				});
				Logger.error("Error saving file record", {}, { dbError });
				setReExportLoading(null);
				return { success: false };
			}

			// Refresh job document to show new file
			await new Promise((resolve) => setTimeout(resolve, 1000));
			await fetchJobDocument(jobDocument.id);

			showNotification({
				message: `${documentTypeName} wurde erfolgreich erneut exportiert.`,
				type: "success",
			});

			setReExportLoading(null);
			return {
				success: true,
				fileId: fileData?.[0]?.id
			};

		} catch (error) {
			Logger.error("Unexpected error during re-export", {}, { error });
			showNotification({
				message: "Ein unerwarteter Fehler ist aufgetreten.",
				type: "error",
			});
			setReExportLoading(null);
			return { success: false };
		}
	};

	return {
		reExportLoading,
		handleReExportDocument,
	};
};
